import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { createRouter, RouterProvider } from '@tanstack/react-router';

import { sessionMemory, sessionQuery } from '@/features/auth/session';
import { setUnauthorizedHandler } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { AppProviders } from '@/providers/AppProviders';
import { routeTree } from '@/routeTree.gen';
import '@/styles/globals.css';

const router = createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    scrollRestoration: true,
});

declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router;
    }
}

// A 401 on a request that expected a session means the session ended under us: the person
// goes back to the login screen as "expired" and returns to this page once signed in again.
setUnauthorizedHandler(() => {
    queryClient.removeQueries({ queryKey: sessionQuery.queryKey });
    const here = router.state.location.href;
    void router.navigate({
        to: '/login',
        search: {
            reason: sessionMemory.hadSession() ? 'expired' : undefined,
            redirect: here.startsWith('/login') ? undefined : here,
        },
    });
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element #root not found');

createRoot(rootElement).render(
    <StrictMode>
        <AppProviders>
            <RouterProvider router={router} />
        </AppProviders>
    </StrictMode>,
);
